/**
 * Справочник VLAN: номер → назначение.
 *
 * Показывает описанные VLAN и те, что встречаются в срезе без описания, —
 * чтобы было видно, что ещё не подписано. Править могут роли с правом
 * редактирования; остальным справочник доступен для чтения.
 */
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../api/client';
import { selectCanEdit } from '../../store/slices/authSlice';
import { loadVlans, selectVlanList } from '../../store/slices/vlansSlice';
import { vlanColor } from '../../domain/ports';
import { cleanVlanId, vlanDirectoryRows } from '../../domain/vlans';
import styles from './VlanDirectory.module.css';

export default function VlanDirectory({ seen, onClose }) {
  const dispatch = useDispatch();
  const canEdit = useSelector(selectCanEdit);
  const list = useSelector(selectVlanList);
  const [drafts, setDrafts] = useState({});
  const [newVid, setNewVid] = useState('');
  const [newText, setNewText] = useState('');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = useMemo(() => vlanDirectoryRows(list, seen), [list, seen]);
  const q = filter.trim().toLowerCase();
  const shown = q ? rows.filter((r) => r.vlan.includes(q) || r.description.toLowerCase().includes(q)) : rows;
  const undescribed = rows.filter((r) => !r.description && r.ports > 0).length;

  async function save(vid, text) {
    const description = text.trim();
    if (!description) { setError('Назначение не может быть пустым — чтобы убрать описание, нажмите «Удалить»'); return false; }
    setBusy(vid);
    setError(null);
    try {
      await api.put(`/api/vlans/${vid}`, { description });
      await dispatch(loadVlans());
      setDrafts((d) => { const n = { ...d }; delete n[vid]; return n; });
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(null);
    }
  }

  async function remove(vid) {
    if (!window.confirm(`Удалить описание VLAN ${vid}?`)) return;
    setBusy(vid);
    setError(null);
    try {
      await api.delete(`/api/vlans/${vid}`);
      await dispatch(loadVlans());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  }

  async function add(e) {
    e.preventDefault();
    const vid = cleanVlanId(newVid);
    if (!vid) { setError('Номер VLAN — число от 1 до 4094'); return; }
    if (await save(vid, newText)) { setNewVid(''); setNewText(''); }
  }

  return (
    <>
      <button className={styles.overlay} onClick={onClose} aria-label="Закрыть" />
      <div className={styles.modal} role="dialog" aria-label="Справочник VLAN">
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Справочник VLAN</div>
            <div className={styles.subtitle}>
              Назначение по номеру — одно на всю сеть. Показывается в разделе «Порты» и в выгрузке CSV.
            </div>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className={styles.toolbar}>
          <input className={styles.search} type="search" value={filter} onChange={(e) => setFilter(e.target.value)}
            placeholder="Номер или назначение" aria-label="Поиск по справочнику" />
          {undescribed > 0 && <span className={styles.warn}>Без описания: {undescribed}</span>}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.body}>
          <table className={styles.table}>
            <thead>
              <tr><th>VLAN</th><th>Назначение</th><th className={styles.num}>Портов в срезе</th>{canEdit && <th />}</tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const draft = drafts[r.vlan];
                const value = draft ?? r.description;
                const changed = draft !== undefined && draft.trim() !== r.description;
                return (
                  <tr key={r.vlan} className={!r.description ? styles.missing : ''}>
                    <td><span className={styles.chip} style={{ '--vlan': vlanColor(r.vlan) }}>{r.vlan}</span></td>
                    <td>
                      {canEdit ? (
                        <input
                          className={styles.input}
                          value={value}
                          placeholder="нет описания"
                          maxLength={200}
                          onChange={(e) => setDrafts((d) => ({ ...d, [r.vlan]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter' && changed) save(r.vlan, value); }}
                          aria-label={`Назначение VLAN ${r.vlan}`}
                        />
                      ) : (r.description || <span className={styles.muted}>нет описания</span>)}
                    </td>
                    <td className={styles.num}>{r.ports ? r.ports.toLocaleString('ru') : <span className={styles.muted}>—</span>}</td>
                    {canEdit && (
                      <td className={styles.actions}>
                        {changed && (
                          <button className={`${styles.button} ${styles.primary}`} disabled={busy === r.vlan}
                            onClick={() => save(r.vlan, value)}>Сохранить</button>
                        )}
                        {r.description && !changed && (
                          <button className={styles.button} disabled={busy === r.vlan} onClick={() => remove(r.vlan)}>Удалить</button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {shown.length === 0 && <div className={styles.none}>{rows.length ? 'Ничего не найдено.' : 'Справочник пуст.'}</div>}
        </div>

        {canEdit && (
          <form className={styles.footer} onSubmit={add}>
            <input className={`${styles.input} ${styles.vidInput}`} value={newVid} onChange={(e) => setNewVid(e.target.value)}
              placeholder="VLAN" inputMode="numeric" aria-label="Номер нового VLAN" />
            <input className={styles.input} value={newText} onChange={(e) => setNewText(e.target.value)}
              placeholder="Назначение, например «Кассы»" maxLength={200} aria-label="Назначение нового VLAN" />
            <button className={`${styles.button} ${styles.primary}`} type="submit" disabled={!newVid.trim() || !newText.trim() || Boolean(busy)}>
              Добавить
            </button>
          </form>
        )}
      </div>
    </>
  );
}
